import { useEffect, useState } from "react";
import axios from "axios";
import { Copy, ArrowsClockwise, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "@/components/ui/sonner";
import { API, formatViews, formatDuration } from "@/lib/api";

const btn = "flex items-center gap-1.5 border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 disabled:opacity-40";
const name = (v) => v.title_override || v.tmdb_title || v.title_clean || v.title;

const CopyRow = ({ v, keep, setKeep, groupKey }) => (
  <label
    data-testid={`dupe-item-${v.video_id}`}
    className={`flex cursor-pointer items-center gap-3 border px-3 py-2 transition-colors duration-200 ${keep ? "border-amber bg-amber/10" : "border-white/10 hover:border-white/30"}`}
  >
    <input type="radio" name={groupKey} checked={keep} onChange={() => setKeep(v.video_id)} data-testid={`dupe-keep-${v.video_id}`} className="accent-amber" />
    <img src={v.default_thumb} alt="" className="h-10 w-16 shrink-0 object-cover" />
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm text-white/85">{v.title}</p>
      <p className="truncate font-mono text-[10px] text-white/40">{v.source_channel || "—"} · {v.video_id}</p>
    </div>
    <span className="font-mono text-[10px] text-white/45">{v.duration_sec ? formatDuration(v.duration_sec) : "—"}</span>
    <span className={`font-mono text-[10px] uppercase ${v.definition === "hd" ? "text-emerald-300" : "text-white/35"}`}>{v.definition || "?"}</span>
    <span className="w-20 text-right font-mono text-[11px] text-white/60">{formatViews(v.views || 0)}</span>
    {keep && <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-amber">Keep</span>}
  </label>
);

const DupeGroup = ({ g, headers, onDone }) => {
  const [keep, setKeep] = useState(g.keep_id);
  const [busy, setBusy] = useState(false);
  const merge = async () => {
    setBusy(true);
    try {
      const ids = g.items.map((v) => v.video_id).filter((id) => id !== keep);
      const { data } = await axios.post(`${API}/admin/duplicates/merge`, { keep_id: keep, merge_ids: ids }, { headers, withCredentials: true });
      toast.success(`Merged ${data.merged} cop${data.merged === 1 ? "y" : "ies"} into one (+${formatViews(data.views_added)} views)`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <li data-testid={`dupe-group-${g.key}`} className="border-b border-white/5 py-4">
      <div className="mb-2 flex items-center gap-3">
        <p className="font-serif text-lg text-[#EDEDED]">{g.title}</p>
        <span className="font-mono text-[10px] text-white/35">{g.year || "Year unknown"} · {g.items.length} copies</span>
        <button data-testid={`dupe-merge-${g.key}`} onClick={merge} disabled={busy} className={`${btn} ml-auto border-amber/60 text-amber hover:bg-amber hover:text-black`}>
          {busy ? "Merging…" : "Merge into selected"}
        </button>
      </div>
      <div className="grid gap-1.5">
        {g.items.map((v) => <CopyRow key={v.video_id} v={v} keep={keep === v.video_id} setKeep={setKeep} groupKey={g.key} />)}
      </div>
    </li>
  );
};

const MergedList = ({ items, headers, onDone }) => {
  const restore = async (id) => {
    try {
      await axios.post(`${API}/admin/duplicates/restore/${id}`, {}, { headers, withCredentials: true });
      toast.success("Copy restored to the catalog");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed");
    }
  };
  if (!items.length) return null;
  return (
    <div data-testid="merged-copies" className="mt-6">
      <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Hidden copies (backup)</p>
      <ul>
        {items.map((v) => (
          <li key={v.video_id} data-testid={`merged-row-${v.video_id}`} className="flex items-center gap-3 border-b border-white/5 py-2">
            <p className="min-w-0 flex-1 truncate text-sm text-white/60">{v.title} <span className="font-mono text-[10px] text-white/30">→ {v.merged_into}</span></p>
            <button data-testid={`merged-restore-${v.video_id}`} onClick={() => restore(v.video_id)} className={`${btn} border-white/20 text-white/70 hover:border-emerald-400 hover:text-emerald-300`}>
              <ArrowCounterClockwise size={13} /> Restore
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const DuplicatesPanel = ({ headers }) => {
  const [groups, setGroups] = useState([]);
  const [merged, setMerged] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const load = async () => {
    setLoaded(false);
    try {
      const [g, m] = await Promise.all([
        axios.get(`${API}/admin/duplicates`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/duplicates/merged`, { headers, withCredentials: true }),
      ]);
      setGroups(g.data.groups);
      setMerged(m.data.items);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load duplicates");
    } finally {
      setLoaded(true);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <section data-testid="duplicates-panel" className="border border-white/10 bg-[#0A0A0A] p-6 sm:p-8">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Copy size={22} weight="duotone" className="text-amber" />
        <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-white/60">Duplicate Cleanup</h2>
        <span data-testid="duplicates-count" className="rounded-full bg-amber px-2 py-0.5 font-mono text-[10px] text-black">{groups.length}</span>
        <button data-testid="duplicates-refresh" onClick={load} className={`${btn} ml-auto border-white/20 text-white/70 hover:border-amber hover:text-amber`}>
          <ArrowsClockwise size={13} /> Refresh
        </button>
      </div>
      <p className="mb-2 font-mono text-[11px] text-white/35">
        Same title + release year. The most-viewed copy (then HD) is pre-selected; merging adds the other copies' views to it and hides them (restorable below).
      </p>
      {!loaded && <p className="py-4 font-mono text-[12px] text-white/40">Scanning catalog…</p>}
      {loaded && groups.length === 0 && <p data-testid="duplicates-empty" className="py-4 font-mono text-[12px] text-emerald-300/80">No duplicates found.</p>}
      <ul>{groups.map((g) => <DupeGroup key={g.key} g={g} headers={headers} onDone={load} />)}</ul>
      <MergedList items={merged} headers={headers} onDone={load} />
    </section>
  );
};
